import { EMPTY_OBJECT } from '../../../constants/common'
import {
  LIST_ROUTE_NAMES,
  ROUTE_UI_SECTION_NAME,
  SUBMENU_PARAM_ARCHIVE_VALUE,
  WEBAPP_SEARCH_ENTITY,
} from '../../../constants/route'
import {
  getBuildingProfilePathname,
  getEmailProfilePathname,
  getFlatProfilePathname,
  getInhabitantProfilePathname,
  getPostProfilePathname,
  getProviderProfilePathname,
  getRequestProfilePathname,
} from '../../../utils/link'
import { getWebappLinkPrefix } from '../../../utils/route'
import { setSearchValueText } from '../../Search'

import {
  SEARCH_BUILDINGS_CONFIG,
  SEARCH_BUILDINGS_TYPE,
  SEARCH_CONTRACTORS_CONFIG,
  SEARCH_CONTRACTORS_TYPE,
  SEARCH_EMAILS_CONFIG,
  SEARCH_FLATS_CONFIG,
  SEARCH_FLATS_TYPE,
  SEARCH_POSTS_CONFIG,
  SEARCH_POSTS_TYPE,
  SEARCH_REQUESTS_CONFIG,
  SEARCH_USERS_CONFIG,
  SEARCH_USERS_TYPE,
} from './LayoutSearch.constants'

/**
 * @param {SearchValue} search
 * @param {object} availableSections
 * @returns { string | null}
 */
export function getSearchPagePath(search, availableSections) {
  const searchText = search.text.trim()

  if (!searchText) {
    return null
  }

  const { links, sectionName, webappEntity } = search.type ?? EMPTY_OBJECT
  const isResultsPageAvailable =
    !sectionName || !!availableSections?.[sectionName]

  if (isResultsPageAvailable) {
    return links?.page?.(setSearchValueText(search, searchText)) ?? null
  }

  return webappEntity
    ? getWebappLinkPrefix(
        `${webappEntity}/search/${encodeURIComponent(searchText)}`
      )
    : null
}

/**
 * @param {object} props
 * @param {object} props.availableSections
 * @param {boolean} [props.withArchivedSearch]
 * @returns {SearchType}
 */
export function getSearchEmailsType({
  availableSections,
  withArchivedSearch = false,
}) {
  return {
    links: {
      item: item =>
        getEmailProfilePathname({
          availableSections,
          id: item.id,
          singleEmailAccount: true,
          sub: item.archived ? SUBMENU_PARAM_ARCHIVE_VALUE : null,
        }),
      page: search =>
        LIST_ROUTE_NAMES.searchResultsEmails({
          archived: search.archived,
          query: search.text,
        }),
    },
    sectionName: ROUTE_UI_SECTION_NAME.mailsSearch,
    title: 'EmailTable.PageTitleKey',
    webappEntity: 'mails',
    withArchivedSearch,
  }
}

/**
 * @param {object} props
 * @param {object} props.availableSections
 * @param {boolean} [props.withArchivedSearch]
 * @returns {SearchType}
 */
export function getSearchRequestsType({
  availableSections,
  withArchivedSearch = false,
}) {
  return {
    links: {
      item: item =>
        getRequestProfilePathname({ availableSections, id: item.id }),
      page: search =>
        LIST_ROUTE_NAMES.searchResultsRequests({
          archived: search.archived,
          query: search.text,
        }),
    },
    sectionName: ROUTE_UI_SECTION_NAME.serviceRequestsSearch,
    title: 'RequestTable.PageTitleKey',
    webappEntity: WEBAPP_SEARCH_ENTITY.serviceRequests,
    withArchivedSearch,
  }
}

/**
 * @param {object} props
 * @param {object} props.availableSections
 * @param {boolean} [props.withArchivedSearch]
 * @returns {SearchType}
 */
export function getSearchBuildingsType({
  availableSections,
  withArchivedSearch = false,
}) {
  return {
    ...SEARCH_BUILDINGS_TYPE,
    links: {
      ...SEARCH_BUILDINGS_TYPE.links,
      item: item =>
        getBuildingProfilePathname({ availableSections, id: item.id }),
    },
    withArchivedSearch,
  }
}

/**
 * @param {object} props
 * @param {object} props.availableSections
 * @param {boolean} [props.withArchivedSearch]
 * @returns {SearchType}
 */
export function getSearchFlatsType({
  availableSections,
  withArchivedSearch = false,
}) {
  return {
    ...SEARCH_FLATS_TYPE,
    links: {
      ...SEARCH_FLATS_TYPE.links,
      item: item => getFlatProfilePathname({ availableSections, id: item.id }),
    },
    withArchivedSearch,
  }
}

export function getSearchPostsType(availableSections) {
  return {
    ...SEARCH_POSTS_TYPE,
    links: {
      ...SEARCH_POSTS_TYPE.links,
      item: item =>
        getPostProfilePathname({ availableSections, id: item.uuid }),
    },
  }
}

/**
 * @param {object} props
 * @param {boolean} props.isUserProfileNGAvailable
 * @param {boolean} [props.withArchivedSearch]
 * @returns {SearchType}
 */
export function getSearchUsersType({
  isUserProfileNGAvailable,
  withArchivedSearch = false,
}) {
  return {
    ...SEARCH_USERS_TYPE,
    links: {
      ...SEARCH_USERS_TYPE.links,
      item: item =>
        getInhabitantProfilePathname({
          id: item.id,
          isUserProfileNGAvailable,
        }),
    },
    withArchivedSearch,
  }
}

/**
 * @param {object} props
 * @param {object} props.availableSections
 * @param {SearchType} props.requestsType
 * @param {boolean} [props.withArchivedBuildingsSearch]
 * @param {boolean} [props.withArchivedFlatsSearch]
 * @returns {Map}
 */
export function getUnconfirmedSearchConfigs({
  availableSections,
  requestsType,
  withArchivedBuildingsSearch = false,
  withArchivedFlatsSearch = false,
}) {
  return new Map([
    [requestsType, SEARCH_REQUESTS_CONFIG],
    [
      getSearchBuildingsType({
        availableSections,
        withArchivedSearch: withArchivedBuildingsSearch,
      }),
      SEARCH_BUILDINGS_CONFIG,
    ],
    [
      getSearchFlatsType({
        availableSections,
        withArchivedSearch: withArchivedFlatsSearch,
      }),
      SEARCH_FLATS_CONFIG,
    ],
  ])
}

/**
 * @param {object} props
 * @param {object} props.availableSections
 * @param {SearchType} props.requestsType
 * @param {boolean} [props.withArchivedBuildingsSearch]
 * @param {boolean} [props.withArchivedFlatsSearch]
 * @returns {Map}
 */
export function getSearchConfigs({
  availableSections,
  requestsType,
  withArchivedBuildingsSearch = false,
  withArchivedFlatsSearch = false,
}) {
  return new Map([
    ...getUnconfirmedSearchConfigs({
      availableSections,
      requestsType,
      withArchivedBuildingsSearch,
      withArchivedFlatsSearch,
    }),
    [getSearchPostsType(availableSections), SEARCH_POSTS_CONFIG],
  ])
}

/**
 * @param {object} props
 * @param {boolean} props.isProviderProfileNGAvailable
 * @param {boolean} [props.withArchivedSearch]
 * @returns {SearchType}
 */
export function getSearchContractorsType({
  isProviderProfileNGAvailable,
  withArchivedSearch = false,
}) {
  return {
    ...SEARCH_CONTRACTORS_TYPE,
    links: {
      ...SEARCH_CONTRACTORS_TYPE.links,
      item: item =>
        getProviderProfilePathname({
          id: item.id,
          isProviderProfileNGAvailable,
        }),
    },
    withArchivedSearch,
  }
}

/**
 * @param {object} props
 * @param {object} props.availableSections
 * @param {boolean} props.isProviderProfileNGAvailable
 * @param {boolean} props.isUserProfileNGAvailable
 * @param {SearchType} props.requestsType
 * @param {boolean} [props.withArchivedBuildingsSearch]
 * @param {boolean} [props.withArchivedContractorsSearch]
 * @param {boolean} [props.withArchivedEmailsSearch]
 * @param {boolean} [props.withArchivedFlatsSearch]
 * @param {boolean} [props.withArchivedUsersSearch]
 * @returns {Map}
 */
export function getStaffSearchConfigs({
  availableSections,
  isProviderProfileNGAvailable,
  isUserProfileNGAvailable,
  requestsType,
  withArchivedBuildingsSearch = false,
  withArchivedContractorsSearch = false,
  withArchivedEmailsSearch = false,
  withArchivedFlatsSearch = false,
  withArchivedUsersSearch = false,
}) {
  return new Map([
    ...getSearchConfigs({
      availableSections,
      requestsType,
      withArchivedBuildingsSearch,
      withArchivedFlatsSearch,
    }),
    [
      getSearchEmailsType({
        availableSections,
        withArchivedSearch: withArchivedEmailsSearch,
      }),
      SEARCH_EMAILS_CONFIG,
    ],
    [
      getSearchUsersType({
        isUserProfileNGAvailable,
        withArchivedSearch: withArchivedUsersSearch,
      }),
      SEARCH_USERS_CONFIG,
    ],
    [
      getSearchContractorsType({
        isProviderProfileNGAvailable,
        withArchivedSearch: withArchivedContractorsSearch,
      }),
      SEARCH_CONTRACTORS_CONFIG,
    ],
  ])
}
